import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {
    ECEntitySubtype,
    ECJuridicalType,
    ECLegalForm,
    ECMemberAccessType,
    ECMemberParticipationType,
    ECMemberState,
    ECSubjectType,
    PlantStatisticalProfile,
    PodStatisticalProfile
} from 'smag-commons/cfg/hierarchy';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ConfigurableField} from 'smag-commons/dto/api';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {DOCUMENT_TYPE_PATTERN} from '../../../shared/patterns';
import {
    ATECO_MAX_CHARS,
    DEFAULT_MAX_CHARS,
    EMAIL_MAX_CHARS,
    IBAN_MAX_CHARS,
    LAND_REGISTRY_MAX_CHARS,
    MOBILE_MAX_CHARS,
    NAME_MAX_CHARS,
    POD_MAX_CHARS,
    SUBSTATION_MAX_CHARS,
    TAX_ID_MAX_CHARS,
    VAT_NUMBER_MAX_CHARS
} from '../../../shared/limits';
import {DialogService} from '../../../shared/dialog/dialog.service';
import {I18NService} from '../../../i18n/i18n.service';
import {AddressExt} from '../../../shared/google-maps-address-autocomplete/google-maps-address-autocomplete.component';
import {MsgBoxService} from '../../../shared/msg-box.service';
import * as uuid from 'uuid';
import {RateMode} from 'smag-commons/cfg';
import {
    ResetContext as StatisticalProfileResetContext,
    StatisticalProfilePlantResource,
    StatisticalProfilePodResourceType,
    StatisticalProfileResourceType
} from 'src/app/shared/statistical-profile/statistical-profile.component';
import {StatisticalProfilePodResource} from '../../../shared/statistical-profile/statistical-profile.component';
import {ResetContext as PredictiveModelResetContext} from '../../../shared/predictive-model/predictive-model.component';
import {PredictiveModelInfo} from 'smag-commons/cfg/prediction';
import {WizardPodCardMode} from 'src/app/shared/wizard-pod-card/wizard-pod-card.component';
import {getContractualPowerValues, isBlank, isDefined} from '../../../commons/commons';
import {
    FormError,
    getFormControl,
    getIncentiveTypes,
    getNominalPower,
    getPlantType,
    getPodUtilityTypes,
    getStatisticalProfileResourceType,
    hasDate,
    isControlOK,
    mkMemberName,
    mkPredictiveModelPlantResource,
    mkStatisticalProfilePlantResource,
    PLANT_TYPES,
    POD_PHASES,
    POD_TYPES,
    POD_VOLTAGES,
    PodEnergyProfiles,
    WIND_PLANT_TYPE
} from '../../../shared/energy-community';
import {StepperSelectionEvent} from '@angular/cdk/stepper';
import {MatSelectChange} from '@angular/material/select';
import {Member} from '../ec-members.component';
import {mkMemberAddress} from 'smag-commons/fcn';
import {limitDecimals, UPPER_INCENTIVE_BOUNDARY} from '../../../shared/ec-economics';
import {MAX_FILE_MB, mimeTypesNames, validateDocumentType, validateFile} from '../../../shared/ec-members';
import {Country} from 'src/app/shared/countries';
import {MUNICIPALITIES, Municipality} from '../../../shared/municipalities';
import {EC_DOCUMENT_TYPE_OTHER, INTERNAL_DOCUMENT_PREFIX, MemberFormService} from './member-form.service';
import {FormCommunity, MembersInfo, PropertyUnitTriplet} from './new-member.model';
import {
    getDefaultEnergyProfile,
    isConsumerResource,
    isFieldEnabled,
    isPersistentPod,
    isPersistentPropertyUnit,
    isProducerResource,
    isProsumerResource,
    isSeu,
    mkPropertyUnitTriplet,
    plantControls,
    plantPodError,
    plantsFormArray,
    podExists,
    resetPodStatisticalProfile
} from './new-member.utils';


const JURIDICAL_TYPES: ECJuridicalType[] = [
    ECJuridicalType.INDIVIDUAL,
    ECJuridicalType.CORPORATE_ENTITY
];

const LEGAL_FORM_TYPES: ECLegalForm[] = [
    ECLegalForm.ENTREPRENEUR_SOLE_PROPRIETORSHIP_AGRICULTURAL_ENTERPRISE,
    ECLegalForm.OTHER_INDIVIDUALS
];

const SUBJECT_TYPES: ECSubjectType[] = [
    ECSubjectType.TERRITORIAL_BODY_LOCAL_AUTHORITY_OTHER_BODY,
    ECSubjectType.PMI,
    ECSubjectType.ASSOCIATION_WITH_LEGAL_PERSONALITY_UNDER_PRIVATE_LAW,
    ECSubjectType.ASSOCIATED_FIRM_COMPANY_OF_PROFESSIONALS,
    ECSubjectType.CONDOMINIUM,
    ECSubjectType.OTHER
    // ECSubjectType.COMMUNITY  // unnecessary with members
];

const ENTITY_SUBTYPES: ECEntitySubtype[] = [
    ECEntitySubtype.STATE,
    ECEntitySubtype.REGION,
    ECEntitySubtype.PROVINCE,
    ECEntitySubtype.MUNICIPALITY,
    ECEntitySubtype.MUNICIPALITIES_UNION,
    ECEntitySubtype.MOUNTAIN_COMMUNITY,
    ECEntitySubtype.ISLAND_COMMUNITY,
    ECEntitySubtype.METROPOLITAN_CITY,
    ECEntitySubtype.RESEARCH_AND_TRAINING_ORGANIZATION,
    ECEntitySubtype.RELIGIOUS_ORGANIZATION,
    ECEntitySubtype.THIRD_SECTOR_ORGANIZATION,
    ECEntitySubtype.ENVIRONMENT_PREVENTION_ORGANIZATION,
    ECEntitySubtype.LOCAL_ADMINISTRATION_ORGANIZATION
];


const DIALOG_WIDTH = 900;

const MIN_SIGNATORY_AGE = 18;


@Component({
    selector: 'app-new-ec-member',
    templateUrl: './new-ec-member.component.html',
    styleUrls: ['./new-ec-member.component.scss'],
    providers: [MemberFormService]
})
export class NewECMemberComponent implements OnInit, AfterViewInit {
    @ViewChild('deleteDialog') private deleteDialog: TemplateRef<any>;
    @ViewChild('setStartDate') private setStartDateDialog: TemplateRef<any>;
    @ViewChild('setPlantDate') private setPlantDateDialog: TemplateRef<any>;
    @ViewChild('spinner') private spinnerDialog: TemplateRef<any>;
    @ViewChild('adviceDialog') private adviceDialog: TemplateRef<any>;
    @ViewChild('setEndDate') private setEndDateDialog: TemplateRef<any>;

    @Input() community: FormCommunity;
    @Input() member: Member;
    @Output() closeDialog: EventEmitter<boolean> = new EventEmitter<boolean>();

    window = window;

    readonly MOBILE_MAX_CHARS = MOBILE_MAX_CHARS;
    readonly EMAIL_MAX_CHARS = EMAIL_MAX_CHARS;
    readonly POD_MAX_CHARS = POD_MAX_CHARS;
    readonly TAX_ID_MAX_CHARS = TAX_ID_MAX_CHARS;
    readonly VAT_NUMBER_MAX_CHARS = VAT_NUMBER_MAX_CHARS;
    readonly IBAN_MAX_CHARS = IBAN_MAX_CHARS;
    readonly ACCEPTED_MIME_TYPES = mimeTypesNames();
    readonly MAX_FILE_MB: number = MAX_FILE_MB;
    readonly DEFAULT_MAX_CHARS = DEFAULT_MAX_CHARS;
    readonly POD_TYPES = POD_TYPES;
    readonly NAME_MAX_CHARS = NAME_MAX_CHARS;
    readonly ATECO_MAX_CHARS = ATECO_MAX_CHARS;
    readonly LAND_REGISTRY_MAX_CHARS = LAND_REGISTRY_MAX_CHARS;
    readonly SUBSTATION_MAX_CHARS = SUBSTATION_MAX_CHARS;
    readonly UPPER_INCENTIVE_BOUNDARY = UPPER_INCENTIVE_BOUNDARY;
    readonly PLANT_TYPES = PLANT_TYPES;
    readonly EC_DOCUMENT_TYPE_OTHER = EC_DOCUMENT_TYPE_OTHER;
    readonly JURIDICAL_TYPES = JURIDICAL_TYPES;
    readonly LEGAL_FORM_TYPES = LEGAL_FORM_TYPES;
    readonly SUBJECT_TYPES = SUBJECT_TYPES;
    readonly ENTITY_SUBTYPES = ENTITY_SUBTYPES;
    readonly POD_PHASES = POD_PHASES;
    readonly POD_VOLTAGES = POD_VOLTAGES;
    readonly WIND_PLANT_TYPE = WIND_PLANT_TYPE;
    readonly MUNICIPALITIES = MUNICIPALITIES;
    readonly Object = Object;
    readonly ECJuridicalType = ECJuridicalType;
    readonly ECSubjectType = ECSubjectType;
    readonly PodEnergyProfiles = PodEnergyProfiles;
    readonly WizardPodCardMode = WizardPodCardMode;
    readonly FormError = FormError;
    readonly contractualPowerValues = getContractualPowerValues();
    readonly getFormControl = getFormControl;
    readonly getPodUtilityTypes = getPodUtilityTypes;
    readonly limitDecimals = limitDecimals;
    readonly getIncentiveTypes = getIncentiveTypes;
    readonly hasDate = hasDate;
    readonly mkPredictiveModelPlantResource = mkPredictiveModelPlantResource;
    readonly mkStatisticalProfilePlantResource = mkStatisticalProfilePlantResource;
    readonly isControlOK = isControlOK;
    readonly isDefined = isDefined;
    readonly ConfigurableField = ConfigurableField;
    readonly isPersistentPod = isPersistentPod;
    readonly plantControls = plantControls;
    readonly isPersistentPropertyUnit = isPersistentPropertyUnit;
    readonly plantPodError = plantPodError;
    readonly isProducerResource = isProducerResource;

    constructor(private readonly dialogService: DialogService,
                private readonly i18n: I18NService,
                private readonly msgBoxService: MsgBoxService,
                private readonly fb: FormBuilder,
                private readonly memberFormService: MemberFormService) {
    }

    ngOnInit() {
        this.memberFormService.init(this.member, this.community, this.closeDialog);
    }

    ngAfterViewInit() {
        this.memberFormService.initDialogs(this.spinnerDialog, this.adviceDialog);
    }

    get accessTypes() {
        return this.memberFormService.accessTypes;
    }

    get accessTypeFormControl() {
        return this.memberFormService.accessTypeFormControl;
    }

    get energyDataStepEnabled() {
        return this.memberFormService.energyDataStepEnabled;
    }

    get statisticalProfileStepEnabled() {
        return this.memberFormService.statisticalProfileStepEnabled;
    }

    get energyDataStepBusy() {
        return this.memberFormService.energyDataStepBusy;
    }

    get memberForm() {
        return this.memberFormService.memberForm;
    }

    get propertyUnitsForm() {
        return this.memberFormService.propertyUnitsForm;
    }

    get podsForm() {
        return this.memberFormService.podsForm;
    }

    get saving() {
        return this.memberFormService.saving;
    }

    get featureConfig() {
        return this.memberFormService.featureConfig;
    }

    get documentTypes() {
        return this.memberFormService.documentTypes;
    }

    get countries() {
        return this.memberFormService.countries;
    }

    get participationTypes() {
        return this.memberFormService.participationTypes;
    }

    get isPropertyUnitDataValid(): boolean {
        return this.cleanPropertyUnitControls.every(c => c.valid);
    }

    get isBiographicalDataStepComplete() {
        return !(!this.memberForm.valid || (!this.memberForm.dirty && !this.isUpdate) || this.continueDisabled);
    }

    get isPropertyUnitDataStepComplete() {
        return this.isPropertyUnitDataValid;
    }

    get isPodDataStepComplete() {
        return this.isPodDataValid;
    }

    get canSaveOnPodDataStep() {
        return !(!this.isPodDataValid || this.saving || !this.hasPropertyUnitsOrPods);
    }

    get isEnergyDataStepComplete() {
        return !(!this.podsForm.valid || (!this.podsForm.dirty && !this.isUpdate) || this.energyDataStepBusy);
    }

    get canSaveOnEnergyDataStep() {
        return !(!this.podsForm.valid || (!this.podsForm.dirty && !this.isUpdate) || this.energyDataStepBusy || this.saving);
    }

    get canSaveOnStatisticalProfileStep() {
        return !(!this.podsForm.valid || (!this.podsForm.dirty && !this.isUpdate) || this.saving);
    }

    get isPodDataValid(): boolean {
        let valid = true;
        for (const podForm of this.podControls) {
            if (!valid) {
                break;
            }
            let checkProducerPods = true;
            // Checks only if there is a POD with no subject assigned
            if (podExists(podForm)) {
                // Mobile app members cannot have producer pods
                if (isProducerResource(podForm) && this.isMobileMember) {
                    checkProducerPods = false;
                }
                // Third party producer cannot have consumer pods
                if (this.isThirdPartyProducer && isConsumerResource(podForm)) {
                    checkProducerPods = false;
                }
                // Third party producer cannot have prosumer pods that are not seu, or it cannot have prosumer pods if seu is disabled
                if (this.isThirdPartyProducer && isProsumerResource(podForm) && (!isSeu(podForm) || !this.memberFormService.featureConfig.seu)) {
                    checkProducerPods = false;
                }
                // Third party producer cannot have producer pods that are seu
                if (this.isThirdPartyProducer && isProducerResource(podForm) && isSeu(podForm)) {
                    checkProducerPods = false;
                }
            }
            // .dirty is not needed to validate step
            const podFormValid = podForm.controls.podCode.valid &&
                (podForm.controls.podAddressLikeMember.value ||
                    (!isBlank(podForm.controls.podAddress.value) && podForm.controls.podAddress.valid));
            const landRegistrySectionFieldsValid = podForm.controls[ConfigurableField.LAND_REGISTRY_SECTION]?.valid ?? true;
            const landRegistrySheetFieldsValid = podForm.controls[ConfigurableField.LAND_REGISTRY_SHEET]?.valid ?? true;
            const landRegistryParticleFieldsValid = podForm.controls[ConfigurableField.LAND_REGISTRY_PARTICLE]?.valid ?? true;
            const landRegistrySubdivisionFieldsValid = podForm.controls[ConfigurableField.LAND_REGISTRY_SUBDIVISION]?.valid ?? true;
            const cadastralDataValid = podForm.controls.podCadastralErrorMessage?.valid ?? true;
            valid = valid && podFormValid && checkProducerPods &&
                landRegistrySectionFieldsValid && landRegistrySheetFieldsValid &&
                landRegistryParticleFieldsValid && landRegistrySubdivisionFieldsValid && cadastralDataValid;
        }
        return valid && !this.continueDisabled;
    }

    get isCompany(): boolean {
        return this.memberFormService.isCompany;
    }

    get juridicalType(): ECJuridicalType {
        return this.memberFormService.juridicalType;
    }

    get isCorporateEntity() {
        return this.memberFormService.isCorporateEntity;
    }

    get legalFormType(): ECLegalForm {
        return this.memberFormService.legalFormType;
    }

    get entitySubtype(): ECEntitySubtype {
        return this.memberFormService.entitySubtype;
    }

    get subjectType(): ECSubjectType {
        return this.memberFormService.subjectType;
    }

    get participationType(): ECMemberParticipationType {
        return this.memberFormService.participationType;
    }

    get isThirdPartyProducer() {
        return this.memberFormService.isThirdPartyProducer;
    }

    get ownersAvailable() {
        return this.memberFormService.ownersAvailable;
    }

    get hasSEU(): boolean {
        return this.memberFormService.hasSEU;
    }

    get isMobileMember() {
        return this.memberFormService.isMobileMember;
    }

    get isUpdate(): boolean {
        return this.memberFormService.isUpdate;
    }

    get invalidCompanyName(): boolean {
        return this.memberFormService.memberForm.controls[ConfigurableField.COMPANY_NAME]?.dirty &&
            this.memberFormService.memberForm.controls[ConfigurableField.COMPANY_NAME]?.invalid;
    }

    get invalidFirstName(): boolean {
        return this.memberFormService.memberForm.controls.firstName.dirty &&
            this.memberFormService.memberForm.controls.firstName.invalid;
    }

    get invalidLastName(): boolean {
        return this.memberFormService.memberForm.controls.lastName.dirty &&
            this.memberFormService.memberForm.controls.lastName.invalid;
    }

    get invalidEmail(): boolean {
        return this.memberFormService.memberForm.controls[ConfigurableField.EMAIL]?.dirty &&
            this.memberFormService.memberForm.controls[ConfigurableField.EMAIL]?.invalid;
    }

    get uniqueEmailError(): boolean {
        return this.memberFormService.memberForm.controls[ConfigurableField.EMAIL]?.hasError('unique_email');
    }

    get invalidConfirmEmail(): boolean {
        return this.memberFormService.memberForm.controls.confirmEmail?.dirty && this.memberFormService.memberForm.controls.confirmEmail?.invalid;
    }

    get invalidMobileNumber(): boolean {
        return this.memberFormService.memberForm.controls[ConfigurableField.MOBILE_NUMBER]?.dirty && this.memberFormService.memberForm.controls[ConfigurableField.MOBILE_NUMBER]?.invalid;
    }

    get notMatchingEmails(): boolean {
        return this.memberFormService.memberForm.controls.confirmEmail?.dirty && this.memberFormService.memberForm.controls.confirmEmail?.hasError(FormError.NOT_MATCHING_EMAILS);
    }

    get addressFormControl(): FormControl {
        return this.memberFormService.addressFormControl;
    }

    get supplyAddressFormControl(): FormControl {
        return this.memberFormService.memberForm.controls[ConfigurableField.SUPPLY_ADDRESS] as FormControl;
    }

    get addressLabel(): string {
        const label = this.juridicalType === ECJuridicalType.CORPORATE_ENTITY ||
        this.juridicalType === ECJuridicalType.INDIVIDUAL && this.legalFormType === ECLegalForm.ENTREPRENEUR_SOLE_PROPRIETORSHIP_AGRICULTURAL_ENTERPRISE ?
            'Registered office address' : 'Residential address';
        return this.mkFieldName(ConfigurableField.ADDRESS, label);
    }

    get birthDateLabel(): string {
        const label = this.juridicalType === ECJuridicalType.INDIVIDUAL ? 'Birth date' : 'Signatory\'s birth date';
        return this.mkFieldName(ConfigurableField.BIRTH_DATE, label);
    }

    get birthCountryLabel(): string {
        const label = this.juridicalType === ECJuridicalType.INDIVIDUAL ? 'Birth country' : 'Signatory\'s birth country';
        return this.mkFieldName(ConfigurableField.BIRTH_COUNTRY, label);
    }

    get birthMunicipalityLabel(): string {
        const label = this.juridicalType === ECJuridicalType.INDIVIDUAL ? 'Birth municipality' : 'Signatory\'s birth municipality';
        return this.mkFieldName(ConfigurableField.BIRTH_MUNICIPALITY, label);
    }

    get getAccessType(): ECMemberAccessType {
        return this.memberFormService.getAccessType;
    }

    get firstNameLabel(): string {
        return this.juridicalType === ECJuridicalType.CORPORATE_ENTITY ? 'Signatory\'s first name' : 'First name';
    }

    get lastNameLabel(): string {
        return this.juridicalType === ECJuridicalType.CORPORATE_ENTITY ? 'Signatory\'s last name' : 'Last name';
    }

    get continueDisabled(): boolean {
        return this.documentControls.some(fg => this.uploadedFileName(fg) === null);
    }

    get showInvitingForm(): boolean {
        return !this.isUpdate ||
            this.member.member.state === ECMemberState.NOT_INVITED ||
            this.member.member.state === ECMemberState.PENDING ||
            this.member.member.state === ECMemberState.CANCELED;
    }

    get enableInvitationInput(): boolean {
        const email = this.memberFormService.memberForm.controls[ConfigurableField.EMAIL];
        const confirmEmail = this.memberFormService.memberForm.controls.confirmEmail;
        const mobile = this.memberFormService.memberForm.controls[ConfigurableField.MOBILE_NUMBER];
        const sendInvitation = this.memberFormService.memberForm.controls.sendInvitation;
        if (email && email.value && email.valid &&
            confirmEmail && confirmEmail.value && confirmEmail.valid &&
            mobile && mobile.value && mobile.valid) {
            return true;
        }
        sendInvitation.setValue(false);
        return false;
    }

    get documentControls(): FormGroup[] {
        return this.memberFormService.documentControls;
    }

    get propertyUnitControls(): FormGroup[] {
        return this.memberFormService.propertyUnitControls;
    }

    get cleanPropertyUnitControls(): FormGroup[] {
        return this.propertyUnitControls.filter(c => !c.controls.forbidden.value);
    }

    get podControls(): FormGroup[] {
        return this.memberFormService.podControls;
    }

    get isNotInvited(): boolean {
        if (this.isUpdate) {
            return this.member.member.state === ECMemberState.NOT_INVITED || this.member.member.state === ECMemberState.CANCELED;
        }
        return false;
    }

    get isPending() {
        if (this.isUpdate) {
            return this.member.member.state === ECMemberState.PENDING;
        }
        return false;
    }

    get memberName(): string {
        return mkMemberName({
            personal_data: {
                first_name: this.memberFormService.memberForm.controls.firstName.value,
                last_name: this.memberFormService.memberForm.controls.lastName.value,
                company_name: this.memberFormService.memberForm.controls[ConfigurableField.COMPANY_NAME]?.value
            }
        });
    }

    get bornInItaly(): boolean {
        return this.memberFormService.bornInItaly;
    }

    get taxIdMaxChars(): number {
        return this.juridicalType === ECJuridicalType.INDIVIDUAL ? TAX_ID_MAX_CHARS : VAT_NUMBER_MAX_CHARS;
    }

    get maxBirthDate(): Moment {
        return moment().startOf('day').subtract(MIN_SIGNATORY_AGE, 'years');
    }

    get communityActivationDate(): Moment | null {
        return this.community && this.community.community && isDefined(this.community.community.activation_date) ?
            moment.tz(this.community.community.activation_date, this.memberFormService.timeZone) :
            null;
    }

    get memberIsOwnerOnly() {
        // member has relations only with property units and not with pods
        return this.memberFormService.podsFormArray.length === 0 && this.memberFormService.propertyUnitsFormArray.length > 0;
    }

    get hasPropertyUnits(): boolean {
        return this.memberFormService.hasPropertyUnits;
    }

    get hasPods(): boolean {
        return this.memberFormService.hasPods;
    }

    get hasOnlyPropertyUnits(): boolean {
        return this.memberFormService.hasOnlyPropertyUnits;
    }

    get hasOnlyPods(): boolean {
        return this.memberFormService.hasOnlyPods;
    }

    get hasPropertyUnitsOrPods(): boolean {
        return this.memberFormService.hasPropertyUnitsOrPods;
    }

    getPodStartDateTooltip(podForm: FormGroup) {
        return this.podHasStartDate(podForm) ?
            this.i18n.translate('GSE defined date. Energy data and incentives will be accounted for starting from that date.') :
            this.i18n.translate('GSE defined date. Energy data and incentives will be accounted for starting from that date. Cannot be entered if the community does not have an activation date.');
    }

    isEnabled(field: ConfigurableField): boolean {
        return isFieldEnabled(this.memberFormService.fieldConfigs, field);
    }

    isMandatory(field: ConfigurableField): boolean {
        return !!this.memberFormService.fieldConfigs?.get(field)?.mandatory;
    }

    mkFieldName(field: ConfigurableField, name: string, separator = ':'): string {
        const fieldCfg = this.memberFormService.fieldConfigs?.get(field);
        return this.i18n.translate(name) + separator + (fieldCfg?.mandatory ? '*' : '');
    }

    getCountryName(country: Country): string {
        return country?.name;
    }

    getMunicipalityName(municipality: Municipality): string {
        return municipality?.name;
    }

    onChangeBirthCountry() {
        const birthMunicipalityCtrl = this.memberFormService.memberForm.controls[ConfigurableField.BIRTH_MUNICIPALITY];
        if (birthMunicipalityCtrl) {
            birthMunicipalityCtrl.setValue(null);
            birthMunicipalityCtrl.markAsPristine();
        }
    }

    getSeuTooltip(podForm: FormGroup) {
        if (isProsumerResource(podForm)) {
            return this.i18n.translate('This POD has a Prosumer profile, therefore it can only use a plant in SEU mode.');
        }
        if (isProducerResource(podForm) || isConsumerResource(podForm)) {
            return this.i18n.translate('SEU mode is not available for this POD.');
        }
        return this.i18n.translate('Select this option if the POD provides a plant for self-consumption by another subject (Utility Efficiency System).');
    }

    hasEntity(podForm: FormGroup): boolean {
        return !!podForm.controls.entityName.value as boolean;
    }

    getControl(form: FormGroup, name: string): FormControl {
        return form.controls[name] as FormControl;
    }

    addressChange(event: AddressExt | null) {
        this.memberFormService.addressChange(event);
    }

    onClickAddDocument() {
        // const id = Math.max(this.documentControls.map(fg => fg.controls.id.value as number)) + 1;
        const formUuid = INTERNAL_DOCUMENT_PREFIX + uuid.v4(); // internal document id
        const documentForm = this.fb.group({
            type: new FormControl('', [Validators.required]),
            customType: new FormControl('', [Validators.pattern(DOCUMENT_TYPE_PATTERN)]),
            id: new FormControl(formUuid, null),
            fileName: new FormControl(null, null)
        }, {
            validators: [validateDocumentType]
        });
        this.memberFormService.documentsFormArray.push(documentForm);
    }

    uploadedFileName(fc: FormGroup) {
        return fc.controls.fileName.value;
    }

    onUploadDocumentFile(documentForm: FormGroup, event: Event) {
        const files = 'files';
        const id = documentForm.controls.id.value as string;
        if (event.target[files] && event.target[files].length) {
            const file: File = event.target[files][0];
            if (validateFile(file, this.msgBoxService, this.i18n)) {
                documentForm.controls.fileName.setValue(file.name);
                this.memberFormService.documents[id] = {
                    name: file.name,
                    documentId: id,
                    file
                };
            }
        } else {
            documentForm.controls.fileName.setValue(null);
        }
        this.updateForm(documentForm);
    }

    hasDocFileError(fg: FormGroup): boolean {
        return fg.hasError('missing_file');
    }

    onClickRemoveDocument(index: number, documentForm: FormGroup) {
        const id = documentForm.controls.id.value;
        this.memberFormService.documents[id] = null;
        this.memberFormService.documentsFormArray.removeAt(index);
    }

    cancel() {
        this.closeDialog.emit(false);
    }

    detectSelectChanges($event: MatSelectChange) {
        if (!this.isUpdate) {
            const value = $event.value as ECMemberParticipationType;
            this.memberFormService.setAccessTypes(value);
            // clear currently selected access type if not allowed among new types
            if (!this.accessTypes.includes(this.accessTypeFormControl.value)) {
                this.accessTypeFormControl.reset();
            }
        }
    }

    selectionChange(selectionEvent: StepperSelectionEvent) {
        const isPuStepEnabled = !this.isThirdPartyProducer && this.ownersAvailable;
        const podDataStepIndex = isPuStepEnabled ? 3 : 2;
        if (selectionEvent.selectedIndex === podDataStepIndex) {
            this.changeEnergyProfile();
            this.memberFormService.searchResourceAndLinkAdminPod().then(() => this.memberFormService.updatePodEnabledSteps());
            this.disableUtilityType();
        }
    }

    canResetPropertyUnitStartDate(form: FormGroup) {
        return isDefined(form.controls.startDate.value);
    }

    canResetPropertyUnitEndDate(form: FormGroup) {
        return isDefined(form.controls.endDate.value);
    }

    canSetPodStartDate(): boolean {
        // community must be active
        return isDefined(this.communityActivationDate);
    }

    canSetPodEndDate(podForm: FormGroup): boolean {
        // a start date must be already defined
        return this.podHasStartDate(podForm);
    }

    podAddressFormControl(fg: FormGroup): FormControl {
        return fg.controls.podAddress as FormControl;
    }

    onClickAddPropertyUnit() {
        this.memberFormService.addPropertyUnit();
    }

    onClickAddPod() {
        this.memberFormService.addPod();
    }

    onClickRemovePropertyUnit(index: number) {
        const propertyUnitForm = this.memberFormService.propertyUnitsFormArray.at(index) as FormGroup;
        if (isPersistentPropertyUnit(propertyUnitForm)) {
            this.dialogService.open<{ propertyUnitTriplet: PropertyUnitTriplet }, boolean>({
                title: this.i18n.translate('Are you sure you want to delete this property unit?'),
                template: this.deleteDialog,
                context: {
                    propertyUnitTriplet: mkPropertyUnitTriplet(propertyUnitForm)
                }
            }).closed.then(del => {
                if (del) {
                    this.memberFormService.propertyUnitsFormArray.removeAt(index);
                }
            });
        } else {
            this.memberFormService.propertyUnitsFormArray.removeAt(index);
        }
    }

    onClickRemovePod(index: number) {
        const podForm = this.memberFormService.podsFormArray.at(index) as FormGroup;
        if (isPersistentPod(podForm)) {
            this.dialogService.open<{ pod: string, mode: 'POD' | 'PU' }, boolean>({
                title: this.i18n.translate('Are you sure you want to delete this POD?'),
                template: this.deleteDialog,
                context: {
                    pod: podForm.controls.podCode.value,
                    mode: 'POD'
                }
            }).closed.then(del => {
                if (del) {
                    this.memberFormService.podsFormArray.removeAt(index);
                }
            });
        } else {
            this.memberFormService.podsFormArray.removeAt(index);
        }
        this.memberFormService.updatePodEnabledSteps();
    }

    addressPodChange(event: AddressExt, fg: FormGroup) {
        fg.controls.internalPodAddress.setValue(mkMemberAddress(event));
        fg.controls.internalPodCoordinates.setValue(event.coordinates);
    }

    podHasStartDate(podForm: FormGroup): boolean {
        return isDefined(podForm.controls.startDate.value);
    }

    podHasEndDate(podForm: FormGroup): boolean {
        return isDefined(podForm.controls.endDate.value);
    }

    onClickResetPropertyUnitStartDate(propertyUnitForm: FormGroup) {
        propertyUnitForm.controls.startDate.setValue(null);
    }

    onClickResetPropertyUnitEndDate(propertyUnitForm: FormGroup) {
        propertyUnitForm.controls.endDate.setValue(null);
    }

    onClickResetPodStartDate(podForm: FormGroup) {
        podForm.controls.startDate.setValue(null);
        // reset end date as well, in case some has been set
        podForm.controls.endDate.setValue(null);
    }

    onClickResetPodEndDate(podForm: FormGroup) {
        podForm.controls.endDate.setValue(null);
    }

    onClickSetPodStartDate(podForm: FormGroup) {
        const minDate = this.podMinStartDate(podForm);
        // we can set start date to community activation date if and only if
        //   - the community has an activation date
        //   - either one of the following conditions holds:
        //     - no minimum date is set
        //     - minimum date precedes (or coincides with) community activation date
        const ecActivationDate = this.communityActivationDate;
        const canSetCommunityActivation = !!ecActivationDate &&
            (!minDate || minDate.isSameOrBefore(ecActivationDate));
        this.dialogService.open<any, Moment>({
            title: this.i18n.translate('POD activation date'),
            template: this.setStartDateDialog,
            context: {
                pod: podForm.controls.podCode.value || '',
                minDate,
                canSetCommunityActivation,
                timezone: this.memberFormService.timeZone
            }
        }, {
            width: DIALOG_WIDTH,
        }).closed.then(startDate => {
            if (!!startDate) {
                podForm.controls.startDate.setValue(startDate.startOf('day'));
            }
        });
    }

    onClickSetPodEndDate(podForm: FormGroup) {
        const minDate = this.podMinEndDate(podForm);
        const today = moment.tz(this.memberFormService.timeZone).startOf('day');
        this.dialogService.open<any, Moment>({
            title: this.i18n.translate('POD deactivation date'),
            template: this.setEndDateDialog,
            context: {
                pod: podForm.controls.podCode.value || '',
                minDate,
                date: new FormControl(minDate.isSameOrAfter(today) ? minDate : today,
                    [Validators.required]) // use "today" as a start
            }
        }).closed.then(endDate => {
            if (!!endDate) {
                podForm.controls.endDate.setValue(endDate.startOf('day'));
            }
        });
    }

    podMinStartDate(podForm: FormGroup): Moment {
        // try to determine the end date of the most recently related member
        // (excluding the currently edited one - if any)
        // NOTE: if we get a `null` here, we assume to have no constraints on minimum date,
        // as a currently "active" relation (i.e. `null` end date) would already have led to the "range taken" scenario,
        // which prevents the user from setting a relation altogether
        const minDate = this.getLatestPodEndDate(podForm.controls.membersInfo.value);
        // for holders, we can never go before community activation date
        if (!!this.communityActivationDate && (!minDate || minDate.isSameOrBefore(this.communityActivationDate))) {
            return this.communityActivationDate;
        }
        return minDate ? minDate.clone().add(1, 'days') : null;
    }

    podMinEndDate(podForm: FormGroup): Moment {
        const startDate: Moment = podForm.controls.startDate.value;
        return startDate ? startDate.clone().add(1, 'days') : null;
    }

    invalidPodProvider(podForm: FormGroup) {
        return podForm.controls.podProvider.dirty && podForm.controls.podProvider.invalid;
    }

    invalidSubstation(podForm: FormGroup) {
        return podForm.controls.substationCode?.dirty && podForm.controls.substationCode?.invalid;
    }

    podEnergyProfile(podForm: FormGroup): PodEnergyProfiles {
        return podForm.controls.podEnergyProfile.value as PodEnergyProfiles;
    }

    mkStatisticalProfilePodResource(podForm: FormGroup): StatisticalProfilePodResource {
        const podCode = podForm.controls.podCode.value;
        const plants: StatisticalProfilePlantResource[] = plantControls(podForm).map(plantForm => ({
            type: getStatisticalProfileResourceType(plantForm.controls.plantType.value),
            name: plantForm.controls.plantName.value as string,
            profile: plantForm.controls.profile.value as PlantStatisticalProfile,
            nominalPower: plantForm.controls.nominalPower.value
        }));
        return {
            type: StatisticalProfileResourceType.POD,
            name: podCode,
            podType: mkStatisticalProfilePodType(this.podEnergyProfile(podForm)),
            plants
        };
    }

    mkPodStatisticalProfileResetValueProvider(): (ctx: StatisticalProfileResetContext) => Promise<PodStatisticalProfile> {
        return () => Promise.resolve(null); // reset statistical profile for POD (consumer or prosumer)
    }

    onClickAddPlant(podForm: FormGroup) {
        this.memberFormService.addPlant(podForm);
    }

    isPlantControlOK(fg: FormGroup, controlName: string) {
        return fg.controls[controlName].dirty && !fg.controls[controlName].valid;
    }

    getPlantDateError(form: FormGroup): string {
        let dateErr = null;
        if (form.controls.enterData.dirty && form.controls.enterData.invalid) {
            dateErr = FormError.BAD_DATE;
        }
        return dateErr;
    }

    onClickResetPlantDate(plantForm: FormGroup) {
        plantForm.controls.enterData.setValue(null);
    }

    onClickSetPlantDate(plantForm: FormGroup) {
        this.dialogService.open<any, Moment>({
            title: this.i18n.translate('Date of commissioning of the plant'),
            template: this.setPlantDateDialog,
            context: {
                timezone: this.memberFormService.timeZone
            }
        }, {
            width: 900
        }).closed.then(effectiveDate => {
            if (effectiveDate) {
                plantForm.controls.enterData.setValue(effectiveDate.startOf('day'));
                plantForm.controls.enterData.disable();
            }
        });
    }

    mkPlantStatisticalProfileResetValueProvider(plantForm: FormGroup): (ctx: StatisticalProfileResetContext) => Promise<PlantStatisticalProfile> {
        return ctx => {
            const nominalPower = plantForm.controls.nominalPower.value;
            return this.memberFormService.mkPlantDefaultStatisticalProfile(nominalPower, ctx.rateMode || RateMode.SINGLE, getPlantType(plantForm)).toPromise();
        };
    }

    mkPlantPredictiveModelResetValueProvider(plantForm: FormGroup): (ctx: PredictiveModelResetContext) => Promise<PredictiveModelInfo> {
        return () => this.memberFormService.mkPlantDefaultPredictiveModel(getNominalPower(plantForm), getPlantType(plantForm)).toPromise();
    }

    onClickRemovePlant(podForm: FormGroup, j: number) {
        plantsFormArray(podForm).removeAt(j);
        plantControls(podForm).forEach(fg => {
            const nameSplit = fg.controls.plantName.value.split(' ');
            const currentNumberPlant = isDefined(nameSplit[nameSplit.length - 1]) ? +nameSplit[nameSplit.length - 1] : 0;
            const newNumberPlant = currentNumberPlant < (j + 1) ? currentNumberPlant : currentNumberPlant - 1;
            fg.controls.plantName.setValue(this.memberFormService.mkPlantName(fg.controls.plantType.value, newNumberPlant));
        });
        resetPodStatisticalProfile(podForm);
    }

    aspiringProsumerForForm(podForm: FormGroup) {
        return podForm.controls.aspiringProsumer.value;
    }

    removeAspiringProsumer(podForm: FormGroup) {
        podForm.controls.aspiringProsumer.setValue(false);
    }

    save() {
        this.memberFormService.save();
    }

    private updateForm(form: FormGroup) {
        for (const ctrl in form.controls) {
            if (form.controls.hasOwnProperty(ctrl)) {
                form.controls[ctrl].updateValueAndValidity({onlySelf: true, emitEvent: true});
            }
        }
        form.updateValueAndValidity({onlySelf: true, emitEvent: true});
    }

    foreignCountry(): boolean {
        return this.memberFormService.foreignCountry();
    }

    private changeEnergyProfile() {
        if (this.isThirdPartyProducer) {
            if (this.hasSEU) {
                this.podControls.forEach(podForm => {
                    podForm.controls.podEnergyProfile.setValue(PodEnergyProfiles.PROSUMER);
                    podForm.controls.podEnergyProfile.disable();
                });
            } else {
                this.podControls.forEach(podForm => {
                    podForm.controls.podEnergyProfile.setValue(PodEnergyProfiles.PRODUCER);
                    podForm.controls.podEnergyProfile.disable();
                });
            }
        } else {
            this.podControls.forEach(podForm => {
                podForm.controls.seu.setValue(null);
                podForm.controls.podEnergyProfile.setValue(getDefaultEnergyProfile(this.memberFormService.isThirdPartyProducer, this.memberFormService.hasSEU));
                podForm.controls.podEnergyProfile.enable();
            });
        }
    }

    private disableUtilityType() {
        this.podControls
            .filter(podForm => isProducerResource(podForm))
            .forEach(podForm => {
                podForm.controls.podUtilityType?.setValue('');
                podForm.controls.podUtilityType?.disable();
            });
    }

    private getLatestPodEndDate(membersInfo: MembersInfo): Moment | null {
        const relations = membersInfo?.members || [];
        // we want to determine the end date of the latest relation *not* involving the current member (if any)
        // no date if there are no relations
        if (!relations.length) {
            return null;
        }
        const currentMemberId = this.member?.member.id; // missing in case of new member
        // the current member is the most recently related one
        if (!!currentMemberId && currentMemberId === relations[0].memberId) {
            // if the current member is the only element in the relation list, then we want to skip it altogether
            if (relations.length === 1) {
                return null;
            }
            // the second element of the list is the most recently related member
            return isDefined(relations[1].end_date) ? moment.tz(relations[1].end_date, this.memberFormService.timeZone) : null;
        }
        // the first element of the list is the most recently related member
        return isDefined(relations[0].end_date) ? moment.tz(relations[0].end_date, this.memberFormService.timeZone) : null;
    }
}

function mkStatisticalProfilePodType(energyProfile: PodEnergyProfiles): StatisticalProfilePodResourceType {
    switch (energyProfile) {
        case PodEnergyProfiles.CONSUMER:
            return StatisticalProfilePodResourceType.CONSUMER;
        case PodEnergyProfiles.PRODUCER:
            return StatisticalProfilePodResourceType.PRODUCER;
        case PodEnergyProfiles.PROSUMER:
            return StatisticalProfilePodResourceType.PROSUMER;
    }
}
